import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Avatar, Typography, Spin, message } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { X } from 'lucide-react';
import { usersService } from '../../services/users.service';
import type { UserUpdateRequest } from '@shared/services/users.service';
import { loadAvatarFromData } from '../../utils/avatarLoader';
import { formatDateDisplay } from '../../utils/dateHelpers';
import styles from './EditHistoryModal.module.css';

const { Text } = Typography;

export interface EditHistoryItem {
  id: string;
  version: number;
  updateDate: string;
  approvalDate?: string;
  rejectionDate?: string;
  approver: {
    name: string;
    avatar?: string;
  };
  status: 'approved' | 'rejected';
}

export interface EditHistoryModalProps {
  open: boolean;
  onClose: () => void;
  userId?: number;
}

// Transform API response sang EditHistoryItem format
// Note: Avatar sẽ được load async trong component, không load ở đây để tránh blocking
const transformToEditHistoryItem = (
  requests: UserUpdateRequest[],
  startVersion: number = 1
): EditHistoryItem[] => {
  return requests
    .filter((req) => req.status === 'approved' || req.status === 'rejected')
    .map((req, index) => {
      const approverInfo = req.approver || req.rejector;
      const approverName = approverInfo?.fullName || 'N/A';
      // Lưu avatar string (relative URL) để load sau bằng loadAvatarFromData
      const approverAvatarString = approverInfo?.avatar || undefined;

      return {
        id: req.id.toString(),
        version: startVersion + index,
        updateDate: formatDateDisplay(req.createdAt),
        approvalDate: req.status === 'approved' ? formatDateDisplay(req.approvedAt) : undefined,
        rejectionDate: req.status === 'rejected' ? formatDateDisplay(req.rejectedAt) : undefined,
        approver: {
          name: approverName,
          avatar: approverAvatarString, // Lưu avatar string để load sau
        },
        status: req.status === 'approved' ? 'approved' : 'rejected',
      };
    });
};

// Component để load avatar của approver
const ApproverAvatar: React.FC<{ avatarString?: string }> = ({ avatarString }) => {
  const [avatarUri, setAvatarUri] = React.useState<string | undefined>(undefined);

  React.useEffect(() => {
    const loadAvatar = async () => {
      if (!avatarString) {
        setAvatarUri(undefined);
        return;
      }
      const loadedAvatar = await loadAvatarFromData(avatarString);
      setAvatarUri(loadedAvatar || undefined);
    };
    loadAvatar();
  }, [avatarString]);

  return (
    <Avatar
      size={24}
      icon={<UserOutlined />}
      src={avatarUri}
      className={styles.avatar}
    />
  );
};

export const EditHistoryModal: React.FC<EditHistoryModalProps> = ({
  open,
  onClose,
  userId,
}) => {
  const navigate = useNavigate();
  const [historyItems, setHistoryItems] = useState<EditHistoryItem[]>([]);
  const [loading, setLoading] = useState(false);

  // Fetch edit history từ API
  useEffect(() => {
    const fetchEditHistory = async () => {
      if (!open || !userId) {
        setHistoryItems([]);
        return;
      }

      try {
        setLoading(true);
        const response = await usersService.getUpdateRequests({
          userId,
          limit: 100, // Lấy tất cả các yêu cầu đã được xử lý
        });

        // Chỉ lấy các yêu cầu đã được duyệt hoặc từ chối
        const processedRequests = response.data.filter(
          (req) => req.status === 'approved' || req.status === 'rejected'
        );

        // Backend trả về theo thứ tự DESC (mới nhất trước)
        // Đảo ngược để đánh số: cũ nhất = "Lần 1", sau đó reverse lại để hiển thị mới nhất trên cùng
        const reversedRequests = [...processedRequests].reverse();
        const transformedItems = transformToEditHistoryItem(reversedRequests);
        // Reverse lại để hiển thị: mới nhất trên cùng, cũ nhất dưới cùng
        setHistoryItems(transformedItems.reverse());
      } catch (error: any) {
        console.error('Error fetching edit history:', error);
        message.error('Không thể tải lịch sử chỉnh sửa. Vui lòng thử lại sau.');
        setHistoryItems([]);
      } finally {
        setLoading(false);
      }
    };

    fetchEditHistory();
  }, [open, userId]);

  const handleViewDetail = (item: EditHistoryItem) => {
    navigate(`/management/personnel/edit-history/${item.version}`, {
      state: {
        version: item.version,
        requestId: item.id,
      },
    });
  };

  const renderDateItem = (label: string, value: string) => (
    <div className={styles.dateItem}>
      <Text className={styles.infoLabel}>{label}</Text>
      <Text className={styles.infoValue}>{value}</Text>
    </div>
  );

  const renderStatusBadge = (status: 'approved' | 'rejected') => {
    if (status === 'approved') {
      return <span className={styles.approvedBadge}>Đã duyệt</span>;
    }
    return <span className={styles.rejectedBadge}>Từ chối</span>;
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      closeIcon={<X size={20} />}
      className={styles.modal}
      width={600}
      destroyOnHidden
    >
      <div className={styles.modalContent}>
        <h2 className={styles.modalTitle}>Lịch sử chỉnh sửa</h2>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin size="large" />
          </div>
        ) : historyItems.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
            <Text>Chưa có lịch sử chỉnh sửa</Text>
          </div>
        ) : (
          <div className={styles.historyList}>
            {historyItems.map((item) => (
            <div
              key={item.id}
              className={styles.historyItem}
              onClick={() => handleViewDetail(item)}
            >
              <div className={styles.versionHeader}>
                <Text className={styles.versionLabel}>Lần {item.version}</Text>
              </div>

              <div className={styles.historyContent}>
                <div className={styles.datesRow}>
                  {renderDateItem('Ngày cập nhật:', item.updateDate)}
                  {item.status === 'approved' &&
                    item.approvalDate &&
                    renderDateItem('Ngày duyệt:', item.approvalDate)}
                  {item.status === 'rejected' &&
                    item.rejectionDate &&
                    renderDateItem('Ngày từ chối:', item.rejectionDate)}
                </div>

                <div className={styles.infoRow}>
                  <Text className={styles.infoLabel}>Người duyệt:</Text>
                  <div className={styles.approverInfo}>
                    <ApproverAvatar avatarString={item.approver.avatar} />
                    <Text className={styles.approverName}>{item.approver.name}</Text>
                  </div>
                </div>
              </div>

              <div className={styles.statusButton}>{renderStatusBadge(item.status)}</div>
            </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
};
